import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import { Stack, Box } from '@atlaskit/primitives';
import { defaultStoreUtils } from '@databases/redux';
import {
  ViewChangeType,
  ViewChangeTypes,
  ViewFilterChangeType,
} from '@databases/utils/database/view/view';
import React, { FunctionComponent, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { useFilterSettings } from '../../hooks/useFilterSettings';
import useView from '../../hooks/useView';
import { messages } from '../../i18n';

import ViewSettingsFilterRow from './ViewSettingsFilterRow';
import ViewSettingsFilterUsagePicker from './ViewSettingsFilterUsagePicker';

export interface ViewSettingsFilterProps {
  contentId: string;
  FieldPicker: React.FunctionComponent<any>;
  FilterTypePicker: React.FunctionComponent<any>;
  FilterValueEdit: React.FunctionComponent<any>;
  autoFocusLastFilterValue?: boolean;
}

const ViewSettingsFilter: FunctionComponent<ViewSettingsFilterProps> = ({
  contentId,
  FieldPicker,
  FilterTypePicker,
  FilterValueEdit,
  autoFocusLastFilterValue,
}) => {
  const { view } = useView();
  const { formatMessage } = useIntl();
  const filterObject = view.settings.filter;
  const store = defaultStoreUtils.useStore();
  const { setViewChange } = useView();
  const { filters, handleChangeFilterType, handleAddFilter } = useFilterSettings({
    onChange: setViewChange,
    filterObject,
    contentId,
    store,
  });

  const rows = useMemo(
    () =>
      filters.map((f, i) => {
        return (
          <ViewSettingsFilterRow
            key={i}
            contentId={contentId}
            value={f}
            autoFocusValue={i === filters.length - 1 && autoFocusLastFilterValue}
            onChange={(change: ViewChangeType) => {
              if (
                change.type === ViewFilterChangeType.VIEW_FILTERS_SET_FIELD_ID ||
                change.type === ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_TYPE ||
                change.type === ViewFilterChangeType.VIEW_FILTERS_SET_VALUE
              ) {
                setViewChange({
                  ...change,
                  path: [i],
                });
              }
            }}
            onRemove={() => {
              setViewChange({
                type: ViewChangeTypes.VIEW_FILTERS_REMOVE,
                path: [i],
              });
            }}
            FieldPicker={FieldPicker}
            FilterTypePicker={FilterTypePicker}
            FilterValueEdit={FilterValueEdit}
          />
        );
      }),
    [
      FieldPicker,
      FilterTypePicker,
      FilterValueEdit,
      autoFocusLastFilterValue,
      contentId,
      filters,
      setViewChange,
    ]
  );

  return (
    <Stack space="space.300">
      {filters.length > 0 && (
        <ViewSettingsFilterUsagePicker handleChangeFilterType={handleChangeFilterType} />
      )}
      {filters.length > 0 && <Stack space="space.200">{rows}</Stack>}
      <Box>
        <Button
          testId="view-settings-panel-filter.add-filter-button"
          iconBefore={
            <AddIcon
              size="small"
              label={formatMessage(
                messages['view-settings-panel-filter.add-filter-button-text']
              )}
            />
          }
          onClick={() => handleAddFilter()}
        >
          {formatMessage(messages['view-settings-panel-filter.add-filter-button-text'])}
        </Button>
      </Box>
    </Stack>
  );
};

export default ViewSettingsFilter;
